import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { IAppState } from 'config/redux/reducers';
import { getFormattedPrice } from 'utils/price.util';
import { isExisty } from 'utils/helpers.util';
import useShipments from 'services/shipments/hooks/useShipments.hook';
import { IShipmentsHook } from 'services/shipments/types/shipments.types';
import useCartReduxKeyName from 'hooks/useCartReduxKeyName';

const getThresholds = (
  minOrderThreshold: number = 0,
  minFoodOrderThreshold: number = 0,
  subTotal: any,
  subtotalFood: any,
  shipments: IShipmentsHook
) => {
  const { isFoodCart, isNonFoodCart } = shipments;

  if (isFoodCart) {
    // FOOD_CART_TYPE
    const { value, currencyName } = subTotal;
    const threshold = Math.max(minOrderThreshold, minFoodOrderThreshold);
    return [
      value < threshold,
      getFormattedPrice(
        {
          currencyName,
          value: threshold,
        },
        false,
        true,
        true
      ),
    ];
  } else if (isNonFoodCart) {
    // NON_FOOD_CART_TYPE
    const { value, currencyName } = subTotal;
    const threshold = minOrderThreshold;
    return [
      value < threshold,
      getFormattedPrice(
        {
          currencyName,
          value: threshold,
        },
        false,
        true,
        true
      ),
    ];
  } else {
    // MIXED_CART
    let threshold = 0;
    if (subtotalFood && subtotalFood.value && minFoodOrderThreshold > subtotalFood.value) {
      threshold = minFoodOrderThreshold;
    }

    const { value: subTotalValue = 0, currencyName } = subTotal;
    if (minOrderThreshold > subTotalValue) {
      threshold += subTotalValue;
    }
    return [
      subtotalFood.value < minFoodOrderThreshold || subTotalValue < minOrderThreshold,
      getFormattedPrice(
        {
          currencyName,
          value: threshold,
        },
        false,
        true,
        true
      ),
      subtotalFood.value < minFoodOrderThreshold, // Reason: isBelowFoodThreshold
      getFormattedPrice(
        {
          // foodThresholdPrice
          currencyName,
          value: minFoodOrderThreshold,
        },
        false,
        true,
        true
      ),
      subTotalValue < minOrderThreshold, // Reason: isBelowCartThreshold
      getFormattedPrice(
        {
          // cartTotalThresholdPrice
          currencyName,
          value: minOrderThreshold,
        },
        false,
        true,
        true
      ),
    ];
  }
};

const useFoodMinimumOrder = (): any[] => {
  const cartKey = useCartReduxKeyName();
  const {
    minFoodOrderThreshold,
    minOrderThreshold,
    subtotalFood,
    subTotal,
    totalItems,
  } = useSelector(({ [cartKey]: { cartData } }: IAppState) => cartData);
  const shipments = useShipments();

  return useMemo(() => {
    if (!isExisty(subTotal) || totalItems === 0 || subTotal.value < 0.01) {
      return [false, 0];
    }

    return getThresholds(
      minOrderThreshold,
      minFoodOrderThreshold,
      subTotal,
      subtotalFood,
      shipments
    );
  }, [
    minFoodOrderThreshold,
    minOrderThreshold,
    subTotal,
    subtotalFood,
    shipments,
    totalItems,
  ]) as any[];
};

export default useFoodMinimumOrder;
